import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Sparkles, CheckCircle2, ChevronRight, MessageSquare, Building2, ShieldCheck,
  Calendar, ArrowRight, Loader2, ArrowLeft, Settings, Send, HelpCircle, ClipboardList,
} from 'lucide-react';
import PartnerListingPreview from '../components/PartnerListingPreview';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PartnerInvitationPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, token: authToken } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);
  // proposal | onboard | calendar | done
  const [step, setStep] = useState('proposal');
  const [approving, setApproving] = useState(false);
  const [selectedListings, setSelectedListings] = useState({});
  const [onb, setOnb] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [calendarStatus, setCalendarStatus] = useState({ connected: false, email: '' });
  const [connectingCalendar, setConnectingCalendar] = useState(false);
  const popupRef = useRef(null);
  const pollIntervalRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Pass the admin's auth token (when available) so the backend's
      // is_internal_user check correctly skips the view counter for admins
      // viewing their own proposal pages.
      const reqHeaders = authToken ? { Authorization: `Bearer ${authToken}` } : {};
      const res = await axios.get(`${API}/partner-invitations/public/${token}`, { headers: reqHeaders });
      setInv(res.data);
      const sel = {};
      (res.data.listings || []).forEach(L => { sel[L.listing_id] = L.approved_by_partner !== false; });
      setSelectedListings(sel);
      setOnb({
        business_legal_name: res.data.partner_legal_name || res.data.partner_business_name || '',
        dba: res.data.partner_dba || '',
        ein: '', tax_id_doc_url: '',
        business_address: res.data.partner_address || '',
        business_city: res.data.partner_city || '',
        business_state: res.data.partner_state || '',
        business_zip: res.data.partner_zip || '',
        business_phone: res.data.partner_phone || '',
        owner_name: res.data.partner_contact_name || '',
        owner_email: res.data.partner_email || '',
        owner_phone: res.data.partner_phone || '',
        logo_url: res.data.partner_logo_url || '',
        cover_url: res.data.partner_cover_url || '',
        bio: res.data.partner_bio || '',
        service_areas: (res.data.partner_service_areas || []).join(', '),
        default_email: res.data.partner_default_email || res.data.partner_email || '',
        booking_sla_hours: 24,
        insurance_doc_url: '', insurance_carrier: '', insurance_policy_number: '', insurance_expires_at: '',
      });
      // Status routing
      if (res.data.status === 'onboarded') {
        // Check if listings still need the final operational details
        try {
          const fr = await axios.get(`${API}/partner-invitations/public/${token}/finalize-status`);
          if (fr.data.all_finalized) setStep('done');
          else setStep('finalize');
        } catch (_) { setStep('done'); }
      }
      else if (res.data.status === 'pending_calendar') setStep('calendar');
      else if (res.data.status === 'approved') setStep('onboard');
    } catch (e) { toast.error('Could not load invitation'); }
    setLoading(false);
  }, [token, authToken]);

  // Force dark theme for proposal pages — they're a customer-facing pitch,
  // and the brand presents better on the dark scheme.
  useEffect(() => {
    const html = document.documentElement;
    const wasDark = html.classList.contains('dark');
    html.classList.add('dark');
    return () => { if (!wasDark) html.classList.remove('dark'); };
  }, []);

  useEffect(() => { load(); }, [load]);

  // After successful calendar return URL handler — also re-route based on finalize-status
  useEffect(() => {
    const connected = searchParams.get('calendar_connected');
    const errorCode = searchParams.get('calendar_error');
    if (connected === 'true') {
      toast.success('Calendar connected — almost done!');
      searchParams.delete('calendar_connected');
      setSearchParams(searchParams, { replace: true });
      load();
    } else if (errorCode) {
      toast.error(`Calendar connection failed (${errorCode}). Try again.`);
      searchParams.delete('calendar_error');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll calendar status while in calendar step (handles popup close without redirect)
  useEffect(() => {
    if (step !== 'calendar') {
      if (pollIntervalRef.current) { clearInterval(pollIntervalRef.current); pollIntervalRef.current = null; }
      return;
    }
    pollIntervalRef.current = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/partner-invitations/public/${token}/calendar-status`);
        setCalendarStatus({ connected: !!r.data.calendar_connected, email: r.data.calendar_email || '' });
        if (r.data.onboarded) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          toast.success('Calendar connected!');
          // Move to finalize step (always, since listings start un-finalized)
          setStep('finalize');
          load();
        }
      } catch (_) { /* swallow */ }
    }, 3000);
    return () => { if (pollIntervalRef.current) clearInterval(pollIntervalRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, token]);

  const updateListing = async (lid, patch) => {
    try {
      // Admin mode: use the unrestricted admin PATCH (any field, any status).
      // Public mode: use the partner PATCH (restricted allowlist + locks once approved).
      if (isAdmin && inv?.invitation_id && authToken) {
        await axios.patch(
          `${API}/partner-invitations/${inv.invitation_id}/listings/${lid}`,
          patch,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
      } else {
        await axios.patch(`${API}/partner-invitations/public/${token}/listings/${lid}`, patch);
      }
      // Optimistic update locally so the modal stays in sync without full reload
      setInv(prev => ({
        ...prev,
        listings: (prev.listings || []).map(L => L.listing_id === lid ? { ...L, ...patch } : L)
      }));
    } catch (e) { toast.error(e.response?.data?.detail || 'Edit failed'); }
  };

  // Admin-only debounced auto-save for proposal fields (tagline, intro, why_eventism, etc.).
  // Each call schedules a save 1.2s after the last change to the same field.
  const autoSaveTimers = useRef({});
  const [savingFields, setSavingFields] = useState({});
  const saveInvField = useCallback((key, value, immediate = false) => {
    if (!isAdmin || !inv?.invitation_id || !authToken) return;
    setInv(prev => ({ ...prev, [key]: value }));  // optimistic
    if (autoSaveTimers.current[key]) clearTimeout(autoSaveTimers.current[key]);
    const fire = async () => {
      setSavingFields(s => ({ ...s, [key]: true }));
      try {
        await axios.patch(
          `${API}/partner-invitations/${inv.invitation_id}`,
          { [key]: value },
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
      } catch (e) {
        toast.error(`Could not save ${key}`);
      }
      setSavingFields(s => ({ ...s, [key]: false }));
    };
    if (immediate) fire(); else autoSaveTimers.current[key] = setTimeout(fire, 1200);
  }, [isAdmin, inv?.invitation_id, authToken]);

  const approve = async () => {
    setApproving(true);
    const ids = Object.entries(selectedListings).filter(([, v]) => v).map(([k]) => k);
    if (ids.length === 0) { toast.error('Approve at least one listing to continue.'); setApproving(false); return; }
    try {
      await axios.post(`${API}/partner-invitations/public/${token}/approve`, { approved_listing_ids: ids });
      toast.success(`${ids.length} listing${ids.length > 1 ? 's' : ''} approved!`);
      setStep('onboard');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Approval failed'); }
    setApproving(false);
  };

  const submitOnboard = async () => {
    if (!onb.business_legal_name || !onb.owner_email) { toast.error('Business legal name and owner email are required.'); return; }
    setSubmitting(true);
    try {
      const payload = { ...onb, service_areas: onb.service_areas.split(',').map(s => s.trim()).filter(Boolean) };
      const res = await axios.post(`${API}/partner-invitations/public/${token}/onboard`, payload);
      if (res.data.calendar_required) {
        toast.success('Almost done — connect your calendar to activate.');
        setStep('calendar');
      } else {
        toast.success('Welcome aboard! Your listings are now live.');
        setStep('done');
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Onboarding failed'); }
    setSubmitting(false);
  };

  const connectCalendar = async () => {
    setConnectingCalendar(true);
    try {
      const r = await axios.get(`${API}/partner-invitations/public/${token}/calendar-auth-url`);
      const url = r.data.authorization_url;
      // Open OAuth in same tab so the redirect works cleanly. Google OAuth flows
      // are flaky inside popups blocked by some browsers; full-page nav is more reliable.
      window.location.href = url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start calendar connection');
      setConnectingCalendar(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (!inv) return <div className="min-h-screen flex items-center justify-center">Invitation not found.</div>;

  // Admin always has full edit power regardless of invitation status.
  const lockedListings = isAdmin
    ? false
    : (inv.status === 'approved' || inv.status === 'pending_calendar' || inv.status === 'onboarded');

  return (
    <div className="min-h-screen bg-background" data-testid="partner-invitation-page">
      {/* Admin toolbar — only visible to logged-in admins */}
      {isAdmin && (
        <div className="sticky top-0 z-30 bg-amber-500/95 backdrop-blur text-amber-950 border-b border-amber-600 shadow-sm" data-testid="admin-toolbar">
          <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 text-sm font-medium">
              <Settings className="w-4 h-4" />
              <span>Admin preview · full edit mode</span>
              <Badge variant="outline" className="bg-white/40 border-amber-700 text-amber-950 text-[10px]">{inv.status}</Badge>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate(`/admin/partner-invitations/${inv.invitation_id}`)}
                className="bg-white/80 border-amber-700 text-amber-950 hover:bg-white"
                data-testid="back-to-editor-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Editor
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate('/admin/partner-invitations')}
                className="bg-white/80 border-amber-700 text-amber-950 hover:bg-white"
                data-testid="admin-list-btn"
              >
                All Invitations
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Hero */}
      <div className="relative overflow-hidden">
        {inv.partner_cover_url && (
          <div className="absolute inset-0 opacity-25">
            <img src={inv.partner_cover_url} alt="" className="w-full h-full object-cover" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-950 via-fuchsia-950/40 to-background" />
        <div className="relative max-w-5xl mx-auto px-4 py-16 sm:py-24">
          <Badge variant="outline" className="mb-4 text-fuchsia-300 border-fuchsia-500/40">Invitation Proposal · {inv.category.toUpperCase()}</Badge>
          <h1 className="text-4xl sm:text-6xl font-bold tracking-tight font-['Space_Grotesk'] mb-4">
            {inv.partner_business_name ? <>Welcome to Eventism, <span className="bg-gradient-to-r from-indigo-400 to-fuchsia-400 bg-clip-text text-transparent">{inv.partner_business_name}</span></> : 'Welcome to Eventism'}
          </h1>
          <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mb-6">
            <AdminInline value={inv.tagline} onSave={(v) => saveInvField('tagline', v)} isAdmin={isAdmin} saving={savingFields.tagline} placeholder="Catchy headline for this proposal" testid="tagline" />
          </p>
          {inv.intro_message && (
            <div className="prose prose-sm dark:prose-invert max-w-none p-4 rounded-lg bg-muted/30 border border-border" dangerouslySetInnerHTML={{ __html: inv.intro_message }} />
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 pb-24 space-y-12">
        {/* Why Eventism */}
        <Section icon={Sparkles} title="Why Eventism">
          <BulletAdmin
            items={inv.why_eventism || []}
            onChange={(arr) => saveInvField('why_eventism', arr, true)}
            isAdmin={isAdmin}
            saving={savingFields.why_eventism}
            renderItem={(text) => (
              <div className="flex gap-3 p-4 rounded-lg bg-card border border-border">
                <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                <span className="text-sm leading-relaxed">{text}</span>
              </div>
            )}
            grid="grid sm:grid-cols-2 gap-3"
            placeholder="Why partners pick Eventism…"
            testid="why-eventism"
          />
          <div className="mt-4 p-5 rounded-xl border border-fuchsia-500/40 bg-gradient-to-r from-fuchsia-500/5 to-indigo-500/5">
            <div className="text-[11px] uppercase tracking-wider text-fuchsia-300 mb-1">What partners earn</div>
            <p className="text-base font-semibold">
              <AdminInline
                value={inv.earnings_example}
                onSave={(v) => saveInvField('earnings_example', v)}
                multiline
                isAdmin={isAdmin}
                saving={savingFields.earnings_example}
                placeholder="e.g. Top DJs book 3-5 weddings/month at $1,200-$3,500 each."
                testid="earnings-example"
              />
            </p>
          </div>
        </Section>

        {/* How it works */}
        <Section icon={ChevronRight} title="How it works">
          <BulletAdmin
            items={inv.how_it_works || []}
            onChange={(arr) => saveInvField('how_it_works', arr, true)}
            isAdmin={isAdmin}
            saving={savingFields.how_it_works}
            renderItem={(text, i) => (
              <li className="flex gap-3">
                <div className="w-7 h-7 rounded-full bg-indigo-500/20 text-indigo-300 flex items-center justify-center text-sm font-bold flex-shrink-0">{i + 1}</div>
                <span className="text-sm leading-7">{text}</span>
              </li>
            )}
            wrapper="ol"
            wrapperClass="space-y-2"
            placeholder="Step the partner takes…"
            testid="how-it-works"
          />
        </Section>

        {/* Listings — live-style cards with pop-out modals */}
        <Section
          icon={Building2}
          title={`${(inv.listings || []).length} listings drafted for you`}
          sub="Click any card to open the full listing — exactly how customers will see it. Edit any field inline."
        >
          {(inv.listings || []).length === 0 ? (
            <p className="text-sm text-muted-foreground">No listings yet. Reply to your contact at Eventism so we can draft some.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="partner-listings-grid">
              {inv.listings.map((L) => (
                <PartnerListingPreview
                  key={L.listing_id}
                  listing={L}
                  partnerCategory={inv.category}
                  selected={!!selectedListings[L.listing_id]}
                  locked={lockedListings}
                  onToggle={() => setSelectedListings(s => ({ ...s, [L.listing_id]: !s[L.listing_id] }))}
                  onPatch={(patch) => updateListing(L.listing_id, patch)}
                  invitationToken={token}
                />
              ))}
            </div>
          )}
          {step === 'proposal' && !lockedListings && (inv.listings || []).length > 0 && (
            <div className="mt-6 space-y-5">
              <WhatsNextPreview />
              <div className="flex justify-end">
                <Button onClick={approve} disabled={approving} className="bg-gradient-to-r from-indigo-600 to-fuchsia-600" data-testid="approve-btn">
                  {approving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                  Approve &amp; Continue
                </Button>
              </div>
            </div>
          )}
        </Section>

        {/* Q&A thread — partners can ask before they approve, admin replies */}
        <CommentThread
          token={token}
          invitationId={inv.invitation_id}
          comments={inv.comments || []}
          isAdmin={isAdmin}
          authToken={authToken}
          onPosted={load}
        />

        {/* FAQ */}
        {((inv.faq || []).length > 0 || isAdmin) && (
          <Section icon={MessageSquare} title="Frequently asked">
            <FaqAdmin
              items={inv.faq || []}
              onChange={(arr) => saveInvField('faq', arr, true)}
              isAdmin={isAdmin}
              saving={savingFields.faq}
            />
          </Section>
        )}

        {/* Onboarding */}
        {step === 'onboard' && (
          <Section icon={ShieldCheck} title="Complete your account" sub="Final step — we already filled in what we know. Verify and add a few more details.">
            <Card><CardContent className="p-5 space-y-4">
              <SubHead>Business</SubHead>
              <div className="grid sm:grid-cols-2 gap-3">
                <OnbField label="Business legal name *" v={onb.business_legal_name} onChange={(v) => setOnb({ ...onb, business_legal_name: v })} testid="onb-legal" />
                <OnbField label="DBA (optional)" v={onb.dba} onChange={(v) => setOnb({ ...onb, dba: v })} />
                <OnbField label="EIN / Tax ID" v={onb.ein} onChange={(v) => setOnb({ ...onb, ein: v })} />
                <OnbField label="W-9 doc URL" v={onb.tax_id_doc_url} onChange={(v) => setOnb({ ...onb, tax_id_doc_url: v })} placeholder="Upload to Drive/Dropbox and paste the link" />
                <OnbField label="Address" v={onb.business_address} onChange={(v) => setOnb({ ...onb, business_address: v })} />
                <OnbField label="City" v={onb.business_city} onChange={(v) => setOnb({ ...onb, business_city: v })} />
                <OnbField label="State" v={onb.business_state} onChange={(v) => setOnb({ ...onb, business_state: v })} />
                <OnbField label="ZIP" v={onb.business_zip} onChange={(v) => setOnb({ ...onb, business_zip: v })} />
                <OnbField label="Business phone" v={onb.business_phone} onChange={(v) => setOnb({ ...onb, business_phone: v })} />
                <OnbField label="Default reply email" v={onb.default_email} onChange={(v) => setOnb({ ...onb, default_email: v })} />
              </div>

              <SubHead>Owner / Contact</SubHead>
              <div className="grid sm:grid-cols-2 gap-3">
                <OnbField label="Owner name" v={onb.owner_name} onChange={(v) => setOnb({ ...onb, owner_name: v })} />
                <OnbField label="Owner email *" v={onb.owner_email} onChange={(v) => setOnb({ ...onb, owner_email: v })} testid="onb-owner-email" />
                <OnbField label="Owner phone" v={onb.owner_phone} onChange={(v) => setOnb({ ...onb, owner_phone: v })} />
              </div>

              <SubHead>Brand & Bio</SubHead>
              <div className="grid sm:grid-cols-2 gap-3">
                <OnbField label="Logo URL" v={onb.logo_url} onChange={(v) => setOnb({ ...onb, logo_url: v })} />
                <OnbField label="Cover URL" v={onb.cover_url} onChange={(v) => setOnb({ ...onb, cover_url: v })} />
                <OnbField label="Service areas (comma-separated)" v={onb.service_areas} onChange={(v) => setOnb({ ...onb, service_areas: v })} />
                <OnbField label="Booking SLA hours" v={onb.booking_sla_hours} onChange={(v) => setOnb({ ...onb, booking_sla_hours: parseInt(v, 10) || 24 })} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Bio</label>
                <Textarea rows={3} value={onb.bio || ''} onChange={(e) => setOnb({ ...onb, bio: e.target.value })} />
              </div>

              <SubHead>Insurance</SubHead>
              <div className="grid sm:grid-cols-2 gap-3">
                <OnbField label="Carrier" v={onb.insurance_carrier} onChange={(v) => setOnb({ ...onb, insurance_carrier: v })} />
                <OnbField label="Policy number" v={onb.insurance_policy_number} onChange={(v) => setOnb({ ...onb, insurance_policy_number: v })} />
                <OnbField label="Certificate URL" v={onb.insurance_doc_url} onChange={(v) => setOnb({ ...onb, insurance_doc_url: v })} placeholder="Paste hosted PDF link" />
                <div>
                  <label className="text-xs text-muted-foreground">Insurance expires</label>
                  <Input type="date" value={onb.insurance_expires_at || ''} onChange={(e) => setOnb({ ...onb, insurance_expires_at: e.target.value })} data-testid="onb-insurance-expires" />
                </div>
              </div>

              <div className="flex justify-end pt-2 border-t border-border">
                <Button onClick={submitOnboard} disabled={submitting} className="bg-gradient-to-r from-indigo-600 to-fuchsia-600" data-testid="submit-onboard-btn">
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
                  Submit &amp; Continue
                </Button>
              </div>
            </CardContent></Card>
          </Section>
        )}

        {/* Calendar gate (mandatory) */}
        {step === 'calendar' && (
          <Section
            icon={Calendar}
            title="Connect your Eventism Calendar"
            sub="One last step — connect Google Calendar so we can sync bookings, prevent double-booking, and keep your availability up-to-date in real time."
          >
            <Card>
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row items-start gap-6">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-fuchsia-500 flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold mb-2">Why we require this</h3>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" /> Customers see live availability when shopping your listings.</li>
                      <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" /> Bookings auto-add to your calendar — no manual entry.</li>
                      <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" /> We never modify events you didn't create.</li>
                    </ul>

                    {calendarStatus.connected ? (
                      <div className="mt-4 p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/40 flex items-center gap-3" data-testid="calendar-connected-banner">
                        <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                        <div className="text-sm">
                          <div className="font-medium">Calendar connected!</div>
                          {calendarStatus.email && <div className="text-muted-foreground text-xs">{calendarStatus.email}</div>}
                        </div>
                      </div>
                    ) : (
                      <Button
                        onClick={connectCalendar}
                        disabled={connectingCalendar}
                        className="mt-5 bg-gradient-to-r from-indigo-600 to-fuchsia-600"
                        size="lg"
                        data-testid="connect-calendar-btn"
                      >
                        {connectingCalendar ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Calendar className="w-4 h-4 mr-2" />}
                        Connect Google Calendar
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </Section>
        )}

        {/* Finalize wizard — partner fills in operational details before listings go live */}
        {step === 'finalize' && (
          <FinalizeWizard
            token={token}
            onComplete={() => { setStep('done'); load(); }}
          />
        )}

        {/* Done */}
        {step === 'done' && (
          <Section icon={CheckCircle2} title="You're in!" sub="Your account is fully active.">
            <div className="p-6 rounded-xl bg-emerald-500/10 border border-emerald-500/40 text-center" data-testid="onboard-done-banner">
              <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-2" />
              <h3 className="text-xl font-bold mb-1">Welcome to Eventism!</h3>
              <p className="text-sm text-muted-foreground">Your listings are live. Check your email for a password setup link to access your dashboard.</p>
            </div>
          </Section>
        )}
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, sub, children }) {
  return (
    <section>
      <h2 className="text-2xl font-bold mb-1 font-['Space_Grotesk'] flex items-center gap-2">
        <Icon className="w-6 h-6 text-fuchsia-400" /> {title}
      </h2>
      {sub && <p className="text-sm text-muted-foreground mb-4">{sub}</p>}
      <div className={sub ? '' : 'mt-4'}>{children}</div>
    </section>
  );
}

function SubHead({ children }) { return <h3 className="text-sm font-semibold uppercase tracking-wider text-fuchsia-300 pt-2">{children}</h3>; }

function OnbField({ label, v, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="text-xs text-muted-foreground">{label}</label>
      <Input value={v || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid} />
    </div>
  );
}

// Local Check icon to avoid importing again at top
function Check(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

/* -------------------------------------------------------------------------- */
/*  "What's next" preview — set partner expectations before they approve      */
/* -------------------------------------------------------------------------- */
function WhatsNextPreview() {
  const steps = [
    { icon: ClipboardList, title: 'Quick onboarding form', body: 'Verify your business details, EIN, and insurance. Most partners finish in under 5 minutes.' },
    { icon: Calendar, title: 'Connect your calendar', body: 'One-click Google Calendar OAuth so we can sync bookings automatically — required to go live.' },
    { icon: ShieldCheck, title: 'Finalize service area & availability', body: 'Tell us your origin ZIP, service radius, available days/hours, and whether you offer drop-off.' },
    { icon: Sparkles, title: 'Listings go live instantly', body: 'The moment you finish, your listings are visible to thousands of Denver-area event planners.' },
  ];
  return (
    <div className="rounded-xl border border-fuchsia-500/30 bg-gradient-to-br from-indigo-500/5 to-fuchsia-500/5 p-5 sm:p-6" data-testid="whats-next-preview">
      <div className="flex items-center gap-2 mb-3">
        <ArrowRight className="w-4 h-4 text-fuchsia-400" />
        <h3 className="text-sm font-bold uppercase tracking-wider text-fuchsia-300">What happens after you approve</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-4">Heads-up so there are no surprises — these are the four short steps before your listings go live.</p>
      <ol className="grid sm:grid-cols-2 gap-3">
        {steps.map((s, i) => {
          const Icon = s.icon;
          return (
            <li key={i} className="flex gap-3 p-3 rounded-lg bg-card border border-border">
              <div className="w-8 h-8 rounded-lg bg-indigo-500/15 text-indigo-300 flex items-center justify-center flex-shrink-0">
                <Icon className="w-4 h-4" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold">{i + 1}. {s.title}</p>
                <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{s.body}</p>
              </div>
            </li>
          );
        })}
      </ol>
      <p className="text-xs text-muted-foreground mt-3 italic">Got a question? Ask us below — we'll reply by email.</p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Q&A comment thread — public partner can ask, logged-in admin can reply    */
/* -------------------------------------------------------------------------- */
function CommentThread({ token, invitationId, comments, isAdmin, authToken, onPosted }) {
  const [text, setText] = useState('');
  const [author, setAuthor] = useState('');
  const [posting, setPosting] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Common partner questions — one-tap inserts so shy partners don't bounce
  // and admins don't answer the same question 50 times.
  const SUGGESTIONS = [
    "What's your commission rate, and when do I get paid out?",
    "Can I keep using my existing booking system, or do I need to migrate?",
    "Do you provide leads exclusively, or am I competing with other partners?",
    "How do refunds and cancellations work — am I on the hook for them?",
    "Can I set my own pricing, or does Eventism control it?",
  ];

  const submit = async () => {
    const body = text.trim();
    if (!body) return;
    setPosting(true);
    try {
      if (isAdmin && invitationId) {
        await axios.post(
          `${API}/partner-invitations/${invitationId}/comment`,
          { body },
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
      } else {
        await axios.post(`${API}/partner-invitations/public/${token}/comment`, {
          text: body,
          author: author.trim() || 'Partner',
        });
      }
      setText('');
      toast.success(isAdmin ? 'Reply posted' : 'Question sent — we\'ll reply by email!');
      onPosted();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not post'); }
    setPosting(false);
  };

  return (
    <Section icon={MessageSquare} title="Questions & Comments" sub={isAdmin ? 'Partner-facing Q&A. Your replies are emailed to the partner and shown on this page.' : 'Anything you want clarified before you approve? Drop your question here — we\'ll reply by email and post the answer below.'}>
      <div className="space-y-3" data-testid="comment-thread">
        {(comments || []).length === 0 && (
          <div className="p-4 rounded-lg border border-dashed border-border text-sm text-muted-foreground flex items-center gap-2">
            <HelpCircle className="w-4 h-4" /> No questions yet. Start the conversation below.
          </div>
        )}
        {(comments || []).map((c) => {
          const isAdminMsg = c.author_type === 'admin';
          return (
            <div
              key={c.id}
              className={`p-4 rounded-lg border ${isAdminMsg ? 'bg-indigo-500/5 border-indigo-500/30' : 'bg-card border-border'}`}
              data-testid={`comment-${c.id}`}
            >
              <div className="flex items-center justify-between text-xs mb-1.5">
                <span className={`font-semibold ${isAdminMsg ? 'text-indigo-300' : 'text-fuchsia-300'}`}>
                  {isAdminMsg ? 'Eventism' : c.author_name}
                  {isAdminMsg && <Badge variant="outline" className="ml-2 border-indigo-500/40 text-indigo-300 text-[9px]">TEAM</Badge>}
                </span>
                <span className="text-muted-foreground">{new Date(c.created_at).toLocaleString()}</span>
              </div>
              <p className="text-sm whitespace-pre-wrap leading-relaxed">{c.body}</p>
            </div>
          );
        })}

        <div className="p-4 rounded-lg border border-fuchsia-500/30 bg-card space-y-2">
          {!isAdmin && (
            <Input
              placeholder="Your name (optional)"
              value={author}
              onChange={(e) => setAuthor(e.target.value)}
              data-testid="comment-author-input"
            />
          )}

          {!isAdmin && (showSuggestions || (!text && (comments || []).length === 0)) && (
            <div className="space-y-1.5" data-testid="suggested-questions">
              <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-fuchsia-300">
                <HelpCircle className="w-3 h-3" /> Common questions partners ask
              </div>
              <div className="flex flex-wrap gap-1.5">
                {SUGGESTIONS.map((s, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => { setText(s); setShowSuggestions(false); }}
                    className="text-xs px-3 py-1.5 rounded-full border border-fuchsia-500/30 bg-fuchsia-500/5 hover:bg-fuchsia-500/15 hover:border-fuchsia-500/60 text-foreground transition text-left"
                    data-testid={`suggestion-${i}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          <Textarea
            rows={3}
            placeholder={isAdmin ? 'Reply to the partner…' : 'Type your question or comment…'}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onFocus={() => !isAdmin && !text && setShowSuggestions(true)}
            data-testid="comment-text-input"
          />
          <div className="flex justify-end">
            <Button onClick={submit} disabled={posting || !text.trim()} className="bg-gradient-to-r from-indigo-600 to-fuchsia-600" data-testid="comment-submit-btn">
              {posting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              {isAdmin ? 'Post reply' : 'Send question'}
            </Button>
          </div>
        </div>
      </div>
    </Section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Admin inline editing helpers — debounced auto-save                        */
/* -------------------------------------------------------------------------- */
function AdminInline({ value, onSave, isAdmin, saving, multiline, placeholder, testid }) {
  const [v, setV] = useState(value || '');
  useEffect(() => setV(value || ''), [value]);
  if (!isAdmin) {
    return value ? <>{value}</> : <span className="text-muted-foreground italic">{placeholder}</span>;
  }
  const Field = multiline ? Textarea : Input;
  return (
    <span className="relative inline-block w-full">
      <Field
        value={v}
        rows={multiline ? 3 : undefined}
        onChange={(e) => { setV(e.target.value); onSave(e.target.value); }}
        placeholder={placeholder}
        className="bg-background/30 border-fuchsia-500/30 focus:border-fuchsia-500"
        data-testid={testid}
      />
      {saving && <Loader2 className="w-3 h-3 absolute -top-2 -right-2 animate-spin text-fuchsia-400" />}
    </span>
  );
}

function BulletAdmin({ items, onChange, isAdmin, saving, renderItem, grid, wrapper = 'div', wrapperClass = '', placeholder, testid }) {
  const [list, setList] = useState(items || []);
  useEffect(() => setList(items || []), [items]);

  if (!isAdmin) {
    const Wrap = wrapper;
    return (
      <Wrap className={grid || wrapperClass}>
        {(items || []).map((it, i) => <React.Fragment key={i}>{renderItem(it, i)}</React.Fragment>)}
      </Wrap>
    );
  }

  const upd = (i, val) => { const next = list.map((x, j) => j === i ? val : x); setList(next); onChange(next); };
  const add = () => { const next = [...list, '']; setList(next); onChange(next); };
  const remove = (i) => { const next = list.filter((_, j) => j !== i); setList(next); onChange(next); };

  return (
    <div className="space-y-2" data-testid={testid}>
      {list.map((it, i) => (
        <div key={i} className="flex gap-2 items-start">
          <Input value={it} onChange={(e) => upd(i, e.target.value)} placeholder={placeholder} className="bg-background/30 border-fuchsia-500/30" />
          <Button type="button" size="icon" variant="ghost" onClick={() => remove(i)} aria-label="Remove">
            <span className="text-destructive text-lg leading-none">×</span>
          </Button>
        </div>
      ))}
      <div className="flex justify-between items-center pt-1">
        <Button type="button" size="sm" variant="ghost" onClick={add}>+ Add line</Button>
        {saving && <span className="text-xs text-fuchsia-400 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Saving…</span>}
      </div>
    </div>
  );
}

function FaqAdmin({ items, onChange, isAdmin, saving }) {
  const [list, setList] = useState(items || []);
  useEffect(() => setList(items || []), [items]);

  if (!isAdmin) {
    return (
      <div className="space-y-2">
        {items.map((f, i) => (
          <details key={i} className="p-4 rounded-lg border border-border bg-card group">
            <summary className="cursor-pointer font-medium list-none flex items-center justify-between">
              {f.q}<ChevronRight className="w-4 h-4 group-open:rotate-90 transition" />
            </summary>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{f.a}</p>
          </details>
        ))}
      </div>
    );
  }

  const upd = (i, k, v) => { const next = list.map((x, j) => j === i ? { ...x, [k]: v } : x); setList(next); onChange(next); };
  const add = () => { const next = [...list, { q: '', a: '' }]; setList(next); onChange(next); };
  const remove = (i) => { const next = list.filter((_, j) => j !== i); setList(next); onChange(next); };

  return (
    <div className="space-y-2" data-testid="faq-admin">
      {list.map((f, i) => (
        <div key={i} className="p-3 rounded-lg border border-fuchsia-500/30 bg-card space-y-2">
          <Input placeholder="Question" value={f.q || ''} onChange={(e) => upd(i, 'q', e.target.value)} className="font-medium" />
          <Textarea rows={2} placeholder="Answer" value={f.a || ''} onChange={(e) => upd(i, 'a', e.target.value)} />
          <div className="flex justify-end">
            <Button type="button" size="sm" variant="ghost" className="text-destructive h-7 text-xs" onClick={() => remove(i)}>Remove</Button>
          </div>
        </div>
      ))}
      <div className="flex justify-between items-center pt-1">
        <Button type="button" size="sm" variant="ghost" onClick={add}>+ Add FAQ</Button>
        {saving && <span className="text-xs text-fuchsia-400 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Saving…</span>}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Finalize wizard — partner fills operational details before listings go live */
/* -------------------------------------------------------------------------- */
function FinalizeWizard({ token, onComplete }) {
  const [status, setStatus] = useState(null);
  const [details, setDetails] = useState({});  // { lid: { ...fields } }
  const [submitting, setSubmitting] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/partner-invitations/public/${token}/finalize-status`);
      setStatus(r.data);
      // Pull current listing operational defaults to seed the form
      const inv = await axios.get(`${API}/partner-invitations/public/${token}`);
      const seeded = {};
      (inv.data.listings || []).forEach(L => {
        seeded[L.listing_id] = {
          service_zip_code: L.service_zip_code || '',
          service_radius_miles: L.service_radius_miles || 25,
          covered_zip_codes: (L.covered_zip_codes || []).join(', '),
          pricing_model: L.pricing_model || 'per_hour',
          min_hours: L.min_hours || 4,
          max_hours: L.max_hours || 8,
          has_dropoff_option: !!L.has_dropoff_option,
          dropoff_price: L.dropoff_price || '',
          dropoff_delivery_radius: L.dropoff_delivery_radius || '',
          dropoff_minimum_order: L.dropoff_minimum_order || '',
          availability_start: L.availability?.start_time || '08:00',
          availability_end: L.availability?.end_time || '22:00',
          availability_days: L.availability?.days_of_week || [0, 1, 2, 3, 4, 5, 6],
        };
      });
      setDetails(seeded);
    } catch (e) { /* swallow */ }
  }, [token]);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  const set = (lid, k, v) => setDetails(d => ({ ...d, [lid]: { ...(d[lid] || {}), [k]: v } }));

  const toggleDay = (lid, day) => {
    setDetails(d => {
      const cur = (d[lid]?.availability_days || []);
      const has = cur.includes(day);
      return { ...d, [lid]: { ...(d[lid] || {}), availability_days: has ? cur.filter(x => x !== day) : [...cur, day].sort() } };
    });
  };

  const submit = async () => {
    setSubmitting(true);
    const payload = { listings: {} };
    Object.entries(details).forEach(([lid, d]) => {
      payload.listings[lid] = {
        service_zip_code: d.service_zip_code || '',
        service_radius_miles: parseInt(d.service_radius_miles, 10) || 25,
        covered_zip_codes: (d.covered_zip_codes || '').split(',').map(s => s.trim()).filter(Boolean),
        pricing_model: d.pricing_model || 'per_hour',
        pricing_unit_label: d.pricing_model === 'per_hour' ? 'per hour' : (d.pricing_model === 'per_person' ? 'per person' : 'per event'),
        min_hours: parseInt(d.min_hours, 10) || null,
        max_hours: parseInt(d.max_hours, 10) || null,
        has_dropoff_option: !!d.has_dropoff_option,
        dropoff_price: d.dropoff_price ? parseFloat(d.dropoff_price) : null,
        dropoff_delivery_radius: d.dropoff_delivery_radius ? parseInt(d.dropoff_delivery_radius, 10) : null,
        dropoff_minimum_order: d.dropoff_minimum_order ? parseFloat(d.dropoff_minimum_order) : null,
        availability: {
          days_of_week: d.availability_days || [0, 1, 2, 3, 4, 5, 6],
          start_time: d.availability_start || '08:00',
          end_time: d.availability_end || '22:00',
          min_notice_hours: 24,
          blackout_dates: [],
        },
      };
    });
    try {
      await axios.post(`${API}/partner-invitations/public/${token}/finalize`, payload);
      toast.success('Your listings are now live!');
      onComplete();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not finalize');
    }
    setSubmitting(false);
  };

  if (!status) return <div className="text-muted-foreground">Loading…</div>;
  const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  return (
    <Section icon={ShieldCheck} title="Finalize your listings" sub="Last step — tell us where you serve, when you're available, and whether you offer drop-off. Listings go live the moment you submit.">
      <div className="space-y-4" data-testid="finalize-wizard">
        {(status.listings || []).map(s => {
          const d = details[s.listing_id] || {};
          return (
            <Card key={s.listing_id}>
              <CardContent className="p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold">{s.name}</h3>
                    <p className="text-xs text-muted-foreground capitalize">{s.category}</p>
                  </div>
                  {s.is_finalized && <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/40">Live</Badge>}
                </div>

                <div className="grid sm:grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs text-muted-foreground">Origin ZIP</label>
                    <Input value={d.service_zip_code || ''} onChange={(e) => set(s.listing_id, 'service_zip_code', e.target.value)} placeholder="80202" data-testid={`fin-zip-${s.listing_id}`} />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Service radius (miles)</label>
                    <Input type="number" value={d.service_radius_miles || ''} onChange={(e) => set(s.listing_id, 'service_radius_miles', e.target.value)} />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Pricing model</label>
                    <select value={d.pricing_model || 'per_hour'} onChange={(e) => set(s.listing_id, 'pricing_model', e.target.value)} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm" data-testid={`fin-pricing-${s.listing_id}`}>
                      <option value="per_hour">Per hour</option>
                      <option value="flat_rate">Flat rate</option>
                      <option value="per_person">Per person</option>
                      <option value="per_day">Per day</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="text-xs text-muted-foreground">Covered ZIPs (comma-separated — leave blank for radius-only)</label>
                  <Input value={d.covered_zip_codes || ''} onChange={(e) => set(s.listing_id, 'covered_zip_codes', e.target.value)} placeholder="80202, 80203, 80205" />
                </div>

                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-muted-foreground">Available from</label>
                    <Input type="time" value={d.availability_start || '08:00'} onChange={(e) => set(s.listing_id, 'availability_start', e.target.value)} />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Available until</label>
                    <Input type="time" value={d.availability_end || '22:00'} onChange={(e) => set(s.listing_id, 'availability_end', e.target.value)} />
                  </div>
                </div>

                <div>
                  <label className="text-xs text-muted-foreground">Days available</label>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {DAYS.map((label, i) => {
                      const active = (d.availability_days || []).includes(i);
                      return (
                        <button key={i} type="button" onClick={() => toggleDay(s.listing_id, i)}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${active ? 'bg-indigo-500 text-white' : 'bg-muted text-muted-foreground'}`}
                          data-testid={`fin-day-${s.listing_id}-${i}`}
                        >{label}</button>
                      );
                    })}
                  </div>
                </div>

                {d.pricing_model === 'per_hour' && (
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-muted-foreground">Min hours</label>
                      <Input type="number" value={d.min_hours || ''} onChange={(e) => set(s.listing_id, 'min_hours', e.target.value)} />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground">Max hours</label>
                      <Input type="number" value={d.max_hours || ''} onChange={(e) => set(s.listing_id, 'max_hours', e.target.value)} />
                    </div>
                  </div>
                )}

                <div className="border-t border-border pt-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={!!d.has_dropoff_option} onChange={(e) => set(s.listing_id, 'has_dropoff_option', e.target.checked)} data-testid={`fin-dropoff-${s.listing_id}`} />
                    <span className="text-sm font-medium">I also offer drop-off / delivery</span>
                  </label>
                  {d.has_dropoff_option && (
                    <div className="grid sm:grid-cols-3 gap-3 mt-3">
                      <div>
                        <label className="text-xs text-muted-foreground">Drop-off flat price</label>
                        <Input type="number" value={d.dropoff_price || ''} onChange={(e) => set(s.listing_id, 'dropoff_price', e.target.value)} placeholder="$" />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground">Delivery radius (mi)</label>
                        <Input type="number" value={d.dropoff_delivery_radius || ''} onChange={(e) => set(s.listing_id, 'dropoff_delivery_radius', e.target.value)} />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground">Minimum order</label>
                        <Input type="number" value={d.dropoff_minimum_order || ''} onChange={(e) => set(s.listing_id, 'dropoff_minimum_order', e.target.value)} placeholder="$" />
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}

        <div className="flex justify-end pt-2">
          <Button onClick={submit} disabled={submitting} className="bg-gradient-to-r from-indigo-600 to-fuchsia-600" size="lg" data-testid="finalize-submit-btn">
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
            Finalize &amp; Go Live
          </Button>
        </div>
      </div>
    </Section>
  );
}
